import { z } from 'zod';

import { documentDataSchema } from '@/features/ui/DocumentsData/model/schemas';
import { optionalAddressBlockSchema } from '@/features/ui/OptionalAddressBlock/model';

import {
  zodArrayRequiredWithMinMax,
  zodDateString,
  zodPhoneMissingPerson,
  zodPlaceholder,
  zodSelectOption,
  zodStringOptionalWithMinMax,
  zodStringRequired,
  zodStringRequiredWithMinMax,
} from '@/shared/lib/validations';
import { UKRAINE_PHONE_MASK_START } from '@/shared/ui';
import { addressBlockSchema } from '@/shared/ui/AddressBlock/model';

import { FormConstants } from '@/components/Forms/constants';

import {
  HEIGHT_REGEXP,
  MissingPersonStatus,
  OTHER_ANCESTRAL_CONNECTION_ID,
  YesNoSelectOptions,
} from '../lib/constants';

export const ApplicantDataSchema = z.object({
  applicantRegisteredAddress: addressBlockSchema,
  applicantResidenceAddress: z
    .object({
      address_equal: zodPlaceholder,
      country: zodPlaceholder,
      apartment: zodPlaceholder,
      building: zodPlaceholder,
      district: zodPlaceholder,
      house: zodPlaceholder,
      region: zodPlaceholder,
      settlement: zodPlaceholder,
      street: zodPlaceholder,
      zip_code: zodPlaceholder,
    })
    .superRefine((data, ctx) => {
      const { address_equal, ...restData } = data;
      if (address_equal) return;

      const addressResult = addressBlockSchema.safeParse({ ...restData });
      if (!addressResult.success) {
        addressResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['applicantResidenceAddress', ...issue.path],
          });
        });
      }
    }),
  applicantData: z
    .object({
      last_name: zodPlaceholder,
      first_name: zodPlaceholder,
      patronymic: zodPlaceholder,
      birthday: zodPlaceholder,
      sex: zodPlaceholder,
      birth_place: zodPlaceholder,
      without_citizenship: zodPlaceholder,
      citizenship: zodPlaceholder,
      ancestral_connection: zodPlaceholder,
      other_ancestral_connection: zodPlaceholder,
      phones: zodPlaceholder,
      documents_proofs_of_ancestral_connection: zodPlaceholder,
      rnokpp: zodPlaceholder,
    })
    .superRefine((data, ctx) => {
      const {
        last_name,
        first_name,
        patronymic,
        rnokpp,
        birthday,
        birth_place,
        without_citizenship,
        citizenship,
        phones,
        other_ancestral_connection,
        ancestral_connection,
        documents_proofs_of_ancestral_connection,
      } = data;

      const lastNameResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_255 }).safeParse(
        last_name,
      );
      if (!lastNameResult.success) {
        lastNameResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['last_name'],
          });
        });
      }

      const firstNameResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_255 }).safeParse(
        first_name,
      );
      if (!firstNameResult.success) {
        firstNameResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['first_name'],
          });
        });
      }

      const patronymicResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_255 }).safeParse(
        patronymic,
      );
      if (!patronymicResult.success) {
        patronymicResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['patronymic'],
          });
        });
      }

      const rnokppResult = z
        .union([
          zodStringRequired
            .min(8, { message: 'РНОКПП має містити 10 цифр' })
            .max(10, { message: 'РНОКПП має містити 10 цифр' }),
          z.literal(''),
        ])
        .nullish()
        .safeParse(rnokpp);
      if (!rnokppResult.success) {
        rnokppResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['rnokpp'],
          });
        });
      }

      const birthdayResult = zodDateString.safeParse(birthday);
      if (!birthdayResult.success) {
        birthdayResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['birthday'],
          });
        });
      }

      const birthPlaceResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH }).safeParse(
        birth_place,
      );
      if (!birthPlaceResult.success) {
        birthPlaceResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['birth_place'],
          });
        });
      }

      if (without_citizenship?.id === YesNoSelectOptions.NO.id) {
        const citizenshipResult = zodSelectOption.safeParse(citizenship);
        if (!citizenshipResult.success) {
          citizenshipResult.error?.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['citizenship'],
            });
          });
        }
      }

      const withoutCitizenshipResult = zodSelectOption.safeParse(without_citizenship);
      if (!withoutCitizenshipResult.success) {
        withoutCitizenshipResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['without_citizenship'],
          });
        });
      }

      if (Array.isArray(phones)) {
        const phonesResult = zodArrayRequiredWithMinMax(zodPhoneMissingPerson, { max: 4 }).safeParse(phones);

        if (!phonesResult.success) {
          phonesResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['phones', ...issue.path],
            });
          });
        }
      }

      if (!ancestral_connection?.id) {
        const result = zodSelectOption.safeParse(ancestral_connection);

        if (!result?.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['ancestral_connection'],
            });
          });
        }
      } else if (ancestral_connection?.id === OTHER_ANCESTRAL_CONNECTION_ID) {
        const otherAncestralConnectionResult = zodStringRequiredWithMinMax({
          max: FormConstants.INPUT_MAX_LENGTH_50,
        }).safeParse(other_ancestral_connection);

        if (!otherAncestralConnectionResult?.success) {
          otherAncestralConnectionResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['other_ancestral_connection'],
            });
          });
        }
      } else {
        const documentsProofsOfAncestralConnectionResult = documentDataSchema.safeParse(
          documents_proofs_of_ancestral_connection,
        );

        if (!documentsProofsOfAncestralConnectionResult?.success) {
          documentsProofsOfAncestralConnectionResult.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              message: '',
              path: ['documents_proofs_of_ancestral_connection.0.attachments'],
            });
          });
        }
      }
    }),
});

export const AddressSchema = z.object({
  missingPersonRegisteredAddress: addressBlockSchema,
  missingPersonResidenceAddress: z
    .object({
      address_equal: zodPlaceholder,
      country: zodPlaceholder,
      apartment: zodPlaceholder,
      building: zodPlaceholder,
      district: zodPlaceholder,
      house: zodPlaceholder,
      region: zodPlaceholder,
      settlement: zodPlaceholder,
      street: zodPlaceholder,
      zip_code: zodPlaceholder,
    })
    .superRefine((data, ctx) => {
      const { address_equal, ...restData } = data;
      if (address_equal) return;

      const addressResult = addressBlockSchema.safeParse({ ...restData });
      if (!addressResult.success) {
        addressResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['missingPersonResidenceAddress', ...issue.path],
          });
        });
      }
    }),
});

export const MissingPersonSchema = z
  .object({
    missingPerson: z.object({
      last_name: zodPlaceholder,
      first_name: zodPlaceholder,
      patronymic: zodPlaceholder,
      rnokpp: zodPlaceholder,
      birthday: zodPlaceholder,
      sex: zodPlaceholder,
      birth_place: zodPlaceholder,
      without_citizenship: zodPlaceholder,
      citizenship: zodPlaceholder,
      person_status: zodPlaceholder,
      military_service_category: zodPlaceholder,
      military_service_type: zodPlaceholder,
      military_unit: zodPlaceholder,
      military_brigade: zodPlaceholder,
      military_formation: zodPlaceholder,
      military_rank: zodPlaceholder,
      military_call_sign: zodPlaceholder,
      phones: zodPlaceholder,
    }),
  })
  .superRefine((data, ctx) => {
    const {
      last_name,
      first_name,
      patronymic,
      rnokpp,
      birthday,
      sex,
      birth_place,
      without_citizenship,
      citizenship,
      person_status,
      phones,
    } = data.missingPerson;

    const lastNameResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_240 }).safeParse(
      last_name,
    );
    if (!lastNameResult.success) {
      lastNameResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson.last_name'],
        });
      });
    }

    const firstNameResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_240 }).safeParse(
      first_name,
    );
    if (!firstNameResult.success) {
      firstNameResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson.first_name'],
        });
      });
    }

    const patronymicResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_240 }).safeParse(
      patronymic,
    );
    if (!patronymicResult.success) {
      patronymicResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson.patronymic'],
        });
      });
    }

    const rnokppResult = z
      .union([
        zodStringRequired
          .min(10, { message: 'РНОКПП має містити 10 цифр' })
          .max(10, { message: 'РНОКПП має містити 10 цифр' }),
        z.literal(''),
      ])
      .nullish()
      .safeParse(rnokpp);
    if (!rnokppResult.success) {
      rnokppResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson.rnokpp'],
        });
      });
    }

    const birthdayResult = zodDateString.safeParse(birthday);
    if (!birthdayResult.success) {
      birthdayResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson.birthday'],
        });
      });
    }

    const sexResult = zodSelectOption.safeParse(sex);
    if (!sex || !sexResult.success) {
      sexResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'sex'],
        });
      });
    }

    const birthPlaceResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH }).safeParse(
      birth_place,
    );
    if (!birthPlaceResult.success) {
      birthPlaceResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson.birth_place'],
        });
      });
    }

    if (without_citizenship?.id === YesNoSelectOptions.NO.id) {
      const citizenshipResult = zodSelectOption.safeParse(citizenship);
      if (!citizenshipResult.success) {
        citizenshipResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['missingPerson.citizenship'],
          });
        });
      }
    }

    const withoutCitizenshipResult = zodSelectOption.safeParse(without_citizenship);
    if (!withoutCitizenshipResult.success) {
      withoutCitizenshipResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson.without_citizenship'],
        });
      });
    }

    const statusResult = zodSelectOption.safeParse(person_status);
    if (!person_status || !statusResult.success) {
      statusResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'person_status'],
        });
      });
    }

    if (person_status?.id === MissingPersonStatus.military) {
      const militaryFields: Array<[string, string]> = [
        ['military_service_category', 'Ранг'],
        ['military_service_type', 'Вид служби'],
        ['military_unit', 'Частина'],
        ['military_brigade', 'Бригада'],
        ['military_formation', 'Формування'],
        ['military_rank', 'Звання/посада'],
        ['military_call_sign', 'Позивний'],
      ];
      militaryFields.forEach(([key]) => {
        let result;

        const value = (data.missingPerson as any)[key];

        result = zodStringRequiredWithMinMax({ max: 512 }).safeParse(value);

        if (key === 'military_service_type') {
          result = zodSelectOption.safeParse(value);
        }
        if (key === 'military_call_sign') {
          result = zodStringRequiredWithMinMax({ max: 100 }).safeParse(value);
        }

        if (!result.success) {
          result.error.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['missingPerson', key],
            });
          });
        }
      });
    }

    if (
      Array.isArray(phones) &&
      phones.length !== 0 &&
      phones.some((phone: string) => !!phone && phone !== UKRAINE_PHONE_MASK_START)
    ) {
      const phonesResult = zodArrayRequiredWithMinMax(zodPhoneMissingPerson, { max: 4 }).safeParse(phones);

      if (!phonesResult.success) {
        phonesResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['missingPerson.phones', ...issue.path],
          });
        });
      }
    }
  });

export const PersonalFeaturesSchema = z
  .object({
    missingPerson: z.object({
      person_stature: zodPlaceholder,
      person_height: zodPlaceholder,
      person_hair_color: zodPlaceholder,
      person_eye_color: zodPlaceholder,
      personal_baldness: zodPlaceholder,
      personal_foot_size: zodPlaceholder,
      personal_blood_group: zodPlaceholder,
      personal_scars: zodPlaceholder,
      personal_tattoo: zodPlaceholder,
      personal_stretching: zodPlaceholder,
      personal_fractures: zodPlaceholder,
      personal_naevus: zodPlaceholder,
      personal_birthmarks: zodPlaceholder,
      personal_teeth_absence: zodPlaceholder,
      personal_crowns_presence: zodPlaceholder,
      personal_implants_presence: zodPlaceholder,
      personal_other_info: zodPlaceholder,
    }),
  })
  .superRefine((data, ctx) => {
    const {
      person_eye_color,
      person_hair_color,
      person_stature,
      person_height,
      personal_baldness,
      personal_blood_group,
      personal_foot_size,
      personal_fractures,
      personal_naevus,
      personal_scars,
      personal_stretching,
      personal_tattoo,
      personal_teeth_absence,
      personal_crowns_presence,
      personal_implants_presence,
      personal_other_info,
    } = data.missingPerson;

    const eyeColorResult = zodSelectOption.safeParse(person_eye_color);
    if (!eyeColorResult.success) {
      eyeColorResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'person_eye_color'],
        });
      });
    }

    const hairColorResult = zodSelectOption.safeParse(person_hair_color);
    if (!hairColorResult.success) {
      hairColorResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'person_hair_color'],
        });
      });
    }

    const statureResult = zodSelectOption.optional().safeParse(person_stature);
    if (!statureResult.success) {
      statureResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'person_stature'],
        });
      });
    }

    const heightResult = zodStringRequiredWithMinMax({ max: 6 })
      .regex(HEIGHT_REGEXP, { message: 'Тільки цифри з комою чи точкою' })
      .safeParse(person_height);
    if (!heightResult.success) {
      heightResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'person_height'],
        });
      });
    }

    const baldnessResult = zodSelectOption.optional().safeParse(personal_baldness);
    if (!personal_baldness || !baldnessResult.success) {
      baldnessResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_baldness'],
        });
      });
    }

    const bloodGroupResult = zodSelectOption.safeParse(personal_blood_group);
    if (!bloodGroupResult.success) {
      bloodGroupResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_blood_group'],
        });
      });
    }

    const footSizeResult = zodStringRequiredWithMinMax({ max: 4 })
      .regex(HEIGHT_REGEXP, { message: 'Тільки цифри з комою чи точкою' })
      .safeParse(personal_foot_size);
    if (!footSizeResult.success) {
      footSizeResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_foot_size'],
        });
      });
    }

    const fracturesResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_100 }).safeParse(
      personal_fractures,
    );
    if (!fracturesResult.success) {
      fracturesResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_fractures'],
        });
      });
    }

    const naevusResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_100 }).safeParse(
      personal_naevus,
    );
    if (!naevusResult.success) {
      naevusResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_naevus'],
        });
      });
    }

    const scarsResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_100 }).safeParse(
      personal_scars,
    );
    if (!scarsResult.success) {
      scarsResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_scars'],
        });
      });
    }

    const stretchingResult = zodSelectOption.optional().safeParse(personal_stretching);
    if (!stretchingResult.success) {
      stretchingResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_stretching'],
        });
      });
    }

    const tattooResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_500 }).safeParse(
      personal_tattoo,
    );
    if (!tattooResult.success) {
      tattooResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_tattoo'],
        });
      });
    }

    const teethAbsenceResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_200 }).safeParse(
      personal_teeth_absence,
    );
    if (!teethAbsenceResult.success) {
      teethAbsenceResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_teeth_absence'],
        });
      });
    }

    const crownsPresenceResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_100 }).safeParse(
      personal_crowns_presence,
    );
    if (!crownsPresenceResult.success) {
      crownsPresenceResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_crowns_presence'],
        });
      });
    }

    const implantsPresenceResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_100 }).safeParse(
      personal_implants_presence,
    );
    if (!implantsPresenceResult.success) {
      implantsPresenceResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_implants_presence'],
        });
      });
    }

    const otherInfoResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_200 }).safeParse(
      personal_other_info,
    );
    if (!otherInfoResult.success) {
      otherInfoResult.error?.issues.forEach((issue) => {
        ctx.addIssue({
          ...issue,
          path: ['missingPerson', 'personal_other_info'],
        });
      });
    }
  });

export const DisappearanceInfoSchema = z.object({
  missingPersonDisappearanceInfo: z
    .object({
      country: zodPlaceholder,
      apartment: zodPlaceholder,
      district: zodPlaceholder,
      region: zodPlaceholder,
      street: zodPlaceholder,
      disappearance: zodPlaceholder,
      disappearance_date: zodPlaceholder,
      disappearance_circumstances: zodPlaceholder,
      missingPersonStatusId: zodPlaceholder,
    })
    .superRefine((data, ctx) => {
      const { disappearance_circumstances, missingPersonStatusId, disappearance_date, ...restData } = data;

      const addressResult = optionalAddressBlockSchema.safeParse({ ...restData });
      if (!addressResult.success) {
        addressResult.error.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: [...issue.path],
          });
        });
      }

      const disappearanceDateResult = zodDateString.optional().safeParse(disappearance_date);
      if (!disappearanceDateResult.success) {
        disappearanceDateResult.error?.issues.forEach((issue) => {
          ctx.addIssue({
            ...issue,
            path: ['disappearance_date'],
          });
        });
      }
      if (missingPersonStatusId === MissingPersonStatus.civil) {
        const disappearanceCircumstancesResult = zodStringRequiredWithMinMax({
          max: FormConstants.INPUT_MAX_LENGTH_512,
        }).safeParse(disappearance_circumstances);
        if (!disappearanceCircumstancesResult.success) {
          disappearanceCircumstancesResult.error?.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['disappearance_circumstances'],
            });
          });
        }
      } else {
        const disappearanceCircumstancesResult = zodStringOptionalWithMinMax({
          max: FormConstants.INPUT_MAX_LENGTH_512,
        }).safeParse(disappearance_circumstances);
        if (!disappearanceCircumstancesResult.success) {
          disappearanceCircumstancesResult.error?.issues.forEach((issue) => {
            ctx.addIssue({
              ...issue,
              path: ['disappearance_circumstances'],
            });
          });
        }
      }
    }),
});
export const DNAOwnerSchema = z
  .object({
    phones: zodPlaceholder,
    method: zodPlaceholder,
    lastName: zodPlaceholder,
    name: zodPlaceholder,
    patronymic: zodPlaceholder,
    birthday: zodPlaceholder,
    ancestralConnection: zodPlaceholder,
  })
  .superRefine((data, ctx) => {
    const phonesToValidate = Array.isArray(data.phones) ? data.phones : [];
    const phonesResult = zodArrayRequiredWithMinMax(zodPhoneMissingPerson, {
      min: 0,
      max: 4,
    }).safeParse(phonesToValidate.filter((p) => !!p && p !== UKRAINE_PHONE_MASK_START));
    if (!phonesResult.success) {
      phonesResult.error.issues.forEach((issue) =>
        ctx.addIssue({
          ...issue,
          path: ['phones', ...issue.path],
        }),
      );
    }

    const methodResult = zodSelectOption.safeParse(data.method);
    if (!methodResult.success) {
      methodResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['method'] }));
    }

    const lastNameResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_240 }).safeParse(
      data.lastName,
    );
    if (!lastNameResult.success) {
      lastNameResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['lastName'] }));
    }
    const nameResult = zodStringRequiredWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_240 }).safeParse(data.name);
    if (!nameResult.success) {
      nameResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['name'] }));
    }

    const patronymicResult = zodStringOptionalWithMinMax({ max: FormConstants.INPUT_MAX_LENGTH_240 }).safeParse(
      data.patronymic,
    );
    if (!patronymicResult.success) {
      patronymicResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['patronymic'] }));
    }

    const birthdayResult = zodDateString.safeParse(data.birthday);
    if (!birthdayResult.success) {
      birthdayResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['birthday'] }));
    }

    const ancResult = zodSelectOption.safeParse(data.ancestralConnection);
    if (!ancResult.success) {
      ancResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['ancestralConnection'] }));
    }
  });

export const ERDRInfoSchema = z.object({
  erdr: z
    .object({
      number: zodPlaceholder,
      entry_date: zodPlaceholder,
      dna_exists: zodPlaceholder,
      method: zodPlaceholder,
      dna_owners: zodPlaceholder,
      additional_info: zodPlaceholder,
    })
    .superRefine((data, ctx) => {
      const numberResult = zodStringOptionalWithMinMax({
        max: FormConstants.INPUT_MAX_LENGTH_20,
      }).safeParse(data.number);
      if (!numberResult.success) {
        numberResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['number'] }));
      }

      const numbeRegexResult = z
        .string()
        .regex(/^\d{0,20}$/, { message: 'Тільки цифри' })
        .nullish()
        .safeParse(data.number);
      if (!numbeRegexResult.success) {
        numbeRegexResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['number'] }));
      }

      if (data.entry_date != null) {
        const dateResult = zodDateString.safeParse(data.entry_date);
        if (!dateResult.success) {
          dateResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['entry_date'] }));
        }
      }

      if (data.dna_exists) {
        const ownersResult = zodArrayRequiredWithMinMax(DNAOwnerSchema, {
          min: 1,
          max: 4,
        }).safeParse(data.dna_owners);
        if (!ownersResult.success) {
          ownersResult.error.issues.forEach((issue) =>
            ctx.addIssue({
              ...issue,
              path: ['dna_owners', ...issue.path],
            }),
          );
        }
      }

      if (data.additional_info != null) {
        const infoResult = zodStringRequiredWithMinMax({
          max: FormConstants.INPUT_MAX_LENGTH_250,
        }).safeParse(data.additional_info);
        if (!infoResult.success) {
          infoResult.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['additional_info'] }));
        }
      }
    }),
});
